import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getTimezone } from './timezone'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('ko')

interface FormatOptions {
  format?: string
  timezone?: string
}

/**
 * ISO 8601 형식의 날짜 문자열을 원하는 형식으로 변환합니다.
 * @param dateString - ISO 8601 형식의 날짜 문자열 (e.g., "2025-08-11T09:00:00Z")
 * @param options - 포맷 옵션
 * @returns 포맷된 날짜 문자열
 * @example
 * ```typescript
 * // 기본 사용
 * formatDate("2025-08-11T09:00:00Z")
 * // => "2025.08.11. 18:00"
 *
 * // 커스텀 포맷
 * formatDate("2025-08-11T09:00:00Z", { format: "YYYY년 MM월 DD일 HH:mm" })
 * // => "2025년 08월 11일 18:00"
 *
 * // 타임존 지정
 * formatDate("2025-08-11T09:00:00Z", { timezone: "Asia/Seoul" })
 * // => "2025.08.11. 18:00"
 * ```
 */
export function formatDate(dateString: string | Date, options: FormatOptions = {}) {
  const { format = 'YY.MM.DD HH:mm', timezone } = options
  const tz = timezone || getTimezone()

  try {
    return dayjs(dateString).tz(tz).format(format)
  } catch (error) {
    console.error('Date formatting error:', error)
    return '날짜 형식 오류'
  }
}

/**
 * 상대적인 시간을 표시합니다. (예: "3일 전", "방금 전")
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @returns 상대적 시간 문자열
 * @example
 * ```typescript
 * formatRelativeTime("2025-08-11T09:00:00Z")
 * // => "3일 전"
 * ```
 */
export function formatRelativeTime(dateString: string | Date) {
  try {
    return dayjs(dateString).tz(getTimezone()).fromNow()
  } catch (error) {
    console.error('Date formatting error:', error)
    return '날짜 형식 오류'
  }
}

/**
 * 날짜가 유효한지 확인합니다.
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @returns 유효한 날짜인지 여부
 */
export function isValidDate(dateString: string) {
  return dayjs(dateString).isValid()
}

export function getDday(date: Date) {
  const today = dayjs()
  const targetDate = dayjs(date)
  const diff = targetDate.diff(today, 'day')
  return diff
}

/*
 * 두 개의 날짜를 입력받아 총 몇일인지 계산
 */
export function getTotalDays(startDate: string, endDate: string) {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  return end.diff(start, 'day')
}

export function YYMMDD(date: string) {
  return dayjs(date).format('YY.MM.DD')
}

export const formatDateToKST = (date: string): string => {
  return dayjs(date).tz('Asia/Seoul').format('YYYY-MM-DDTHH:mm:ssZ')
}
